<template>
  <div
    :class="['modal-overlay', 'child-modal', { show: show }]"
    v-show="show"
    @click.self="close"
  >
    <div
      class="modal-container child-modal-container"
      :class="{ 'modal-enter-active': show }"
    >
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header d-flex justify-content-between">
          <div class="modal-title-section">
            <div class="modal-title-content">
              <h2 class="modal-title text-light">
                <i class="bi bi-info"></i> {{ title }}
              </h2>
            </div>
          </div>
          <button @click="close" type="button" class="close-btn" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="body-content">
            <div class="notes-container">
              <!-- Notes Header -->
              <div class="notes-header">
                <div class="notes-title">
                  <i class="bi bi-journal-text"></i>
                  <h3>Add New Note</h3>
                </div>
                <div class="notes-actions">
                  <button class="btn-icon" title="Change color">
                    <i class="bi bi-palette"></i>
                  </button>
                  <button class="btn-icon" title="Add attachment">
                    <i class="bi bi-paperclip"></i>
                  </button>
                </div>
              </div>

              <!-- Notes Content -->
              <div class="notes-content">
                <!-- <div class="note-title-section">
                  <input
                    type="text"
                    class="note-title-input"
                    placeholder="Note title..."
                    maxlength="60"
                  />
                  <span class="char-counter">0/60</span>
                </div> -->

                <div class="note-body-section">
                  <textarea
                    class="note-textarea"
                    placeholder="Start typing your note here..."
                    v-model="noteContent"
                    rows="8"
                  ></textarea>

                  <div class="formatting-toolbar">
                    <button class="format-btn" title="Bold">
                      <i class="bi bi-type-bold"></i>
                    </button>
                    <button class="format-btn" title="Italic">
                      <i class="bi bi-type-italic"></i>
                    </button>
                    <button class="format-btn" title="Underline">
                      <i class="bi bi-type-underline"></i>
                    </button>
                    <button class="format-btn" title="Bullet list">
                      <i class="bi bi-list-ul"></i>
                    </button>
                    <button class="format-btn" title="Numbered list">
                      <i class="bi bi-list-ol"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <div class="footer-actions">
            <div class="footer-info">
              <span class="last-saved">Last saved: {{ lastSaved }}</span>
            </div>
            <div class="action-buttons">
              <button class="btn btn-secondary" @click="close">
                <i class="bi bi-x-circle"></i>
                Cancel
              </button>
              <button :disabled="isSaving" class="btn btn-primary" @click="saveNote">
                <i v-if="!isSaving" class="bi bi-check-circle"></i>
                <i v-else class="fas fa-spinner"></i>
                Save Note
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import emergencyService from "@/services/emergencyService";
import { ref, watch } from "vue";
/* eslint-disable */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Details",
  },
  id: {
    type: String,
    default: null,
  },
  notes: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "bi-info-circle-fill",
  },
  iconClass: {
    type: String,
    default: "info",
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  footerText: {
    type: String,
    default: "Quick Actions",
  },
  footerIcon: {
    type: String,
    default: "bi-lightning-fill",
  },
});
const noteContent = ref("");
const lastSaved = ref("Never");
const isSaving = ref(false);
const saveNote = async () => {
  isSaving.value = true;
  emergencyService.addNotes(props.id, noteContent.value);
  lastSaved.value = "Just now";
  isSaving.value = false;
  emit("confirm", noteContent.value);
  setTimeout(() => {
    noteContent.value = "";
  }, 300);
};

const emit = defineEmits(["close", "confirm"]);
const close = () => {
  emit("close");
};

watch(
  () => props.notes,
  (newVal) => {
    if (newVal) {
      noteContent.value = newVal;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
/* Child Modal Specific Styles */
.modal-overlay.child-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1070; /* Higher than parent modal */
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-overlay.child-modal.show {
  opacity: 1;
  visibility: visible;
}

.modal-container.child-modal-container {
  position: fixed; /* Crucial for centering */
  top: 50%; /* Crucial for centering */
  left: 50%; /* Crucial for centering */
  width: 90%;
  max-width: 600px;
  height: auto;
  max-height: 80vh;
  transform: translate(-50%, -50%) scale(0.9);
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 50px rgba(163, 14, 14, 0.1);
  z-index: 1080;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-container.child-modal-container.modal-enter-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* The rest of your existing styles remain the same */
.modal-overlay.child-modal .modal-header {
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #495057 0%, #343a40 100%);
}

.modal-overlay.child-modal .title-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  animation: none;
}

.modal-overlay.child-modal .title-icon i {
  font-size: 1.25rem;
}

.modal-overlay.child-modal .modal-title {
  font-size: 1.25rem;
}

.modal-overlay.child-modal .modal-subtitle {
  font-size: 0.8rem;
}

.modal-overlay.child-modal .modal-body {
  max-height: 50vh;
  overflow-y: auto;
}

.modal-overlay.child-modal .body-content {
  padding: 1.5rem;
}

.modal-overlay.child-modal .modal-footer {
  padding: 1rem 1.5rem;
}

.modal-overlay.child-modal .footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.modal-overlay.child-modal .action-buttons {
  display: flex;
  gap: 0.75rem;
}
/* Button Styles */
.btn {
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(163, 14, 14, 0.3);
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: white;
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  background: linear-gradient(135deg, #5a6268, #495057);
}

.btn:active {
  transform: translateY(0);
}

/* Icon Variants */
.title-icon.info {
  background: rgba(74, 144, 226, 0.2);
  border: 1px solid rgba(74, 144, 226, 0.3);
}

.title-icon.info i {
  color: #4a90e2;
}

.title-icon.warning {
  background: rgba(255, 193, 7, 0.2);
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.title-icon.warning i {
  color: #ffc107;
}

.title-icon.success {
  background: rgba(40, 167, 69, 0.2);
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.title-icon.success i {
  color: #28a745;
}

.title-icon.danger {
  background: rgba(163, 14, 14, 0.2);
  border: 1px solid rgba(163, 14, 14, 0.3);
}

.title-icon.danger i {
  color: #a30e0e;
}

/* Enhanced focus states */
.btn:focus {
  outline: 2px solid rgba(163, 14, 14, 0.5);
  outline-offset: 2px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1) rotate(90deg);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.close-btn:active {
  transform: scale(0.95);
}

.notes-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 1rem;
}

/* Notes Header */
.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.notes-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.notes-title i {
  font-size: 1.5rem;
  color: #a30e0e;
}

.notes-title h3 {
  margin: 0;
  font-weight: 600;
  color: #343a40;
}

.notes-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: rgba(108, 117, 125, 0.1);
  border: 1px solid rgba(108, 117, 125, 0.2);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: rgba(108, 117, 125, 0.2);
  color: #495057;
  transform: translateY(-1px);
}

/* Notes Content */
.notes-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
}

/* Note Title Section */
.note-title-section {
  position: relative;
}

.note-title-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.note-title-input:focus {
  outline: none;
  border-color: #a30e0e;
  box-shadow: 0 0 0 2px rgba(163, 14, 14, 0.1);
}

.char-counter {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #6c757d;
  background: white;
  padding: 0 0.25rem;
}

/* Note Body Section */
.note-body-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.note-textarea {
  width: 100%;
  flex: 1;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  resize: none;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.note-textarea:focus {
  outline: none;
  border-color: #a30e0e;
  box-shadow: 0 0 0 2px rgba(163, 14, 14, 0.1);
}

.formatting-toolbar {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(108, 117, 125, 0.05);
  border-radius: 6px;
}

.format-btn {
  background: transparent;
  border: 1px solid rgba(108, 117, 125, 0.2);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
}

.format-btn:hover {
  background: rgba(108, 117, 125, 0.1);
  color: #495057;
}

.format-btn.active {
  background: rgba(163, 14, 14, 0.1);
  color: #a30e0e;
  border-color: rgba(163, 14, 14, 0.3);
}

/* Notes Tags */
.notes-tags {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tags-input-container {
  display: flex;
  gap: 0.5rem;
}

.tags-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.8);
}

.tags-input:focus {
  outline: none;
  border-color: #a30e0e;
}

.add-tag-btn {
  background: rgba(163, 14, 14, 0.1);
  border: 1px solid rgba(163, 14, 14, 0.2);
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a30e0e;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-tag-btn:hover {
  background: rgba(163, 14, 14, 0.2);
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-height: 1.5rem;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: rgba(163, 14, 14, 0.1);
  border: 1px solid rgba(163, 14, 14, 0.2);
  border-radius: 16px;
  font-size: 0.75rem;
  color: #a30e0e;
}

.tag-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 16px;
  height: 16px;
}

.tag-remove:hover {
  background: rgba(163, 14, 14, 0.2);
}

/* Footer Info */
.footer-info {
  font-size: 0.875rem;
  color: #6c757d;
}

.last-saved {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .notes-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .notes-actions {
    align-self: flex-end;
  }

  .footer-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .footer-info {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .formatting-toolbar {
    flex-wrap: wrap;
    justify-content: center;
  }

  .tags-input-container {
    flex-direction: column;
  }

  .add-tag-btn {
    align-self: flex-end;
    width: 100%;
    max-width: 120px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-container.child-modal-container {
    width: 95%;
    max-width: none;
    margin: 1rem;
    border-radius: 12px;
  }

  .modal-overlay.child-modal .footer-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-overlay.child-modal .action-buttons {
    width: 100%;
    justify-content: stretch;
  }

  .modal-overlay.child-modal .action-buttons .btn {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-overlay.child-modal .modal-header {
    padding: 1rem;
  }

  .modal-overlay.child-modal .body-content {
    padding: 1rem;
  }

  .modal-overlay.child-modal .modal-footer {
    padding: 0.875rem 1rem;
  }

  .btn {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
}

/* Smooth backdrop for nested modals */
.modal-overlay.child-modal::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at center,
    rgba(163, 14, 14, 0.05) 0%,
    transparent 70%
  );
}
</style>
